import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  TextField,
  Checkbox,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Grid,
  Divider,
  Chip,
  ButtonGroup,
  CircularProgress,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
} from '@mui/icons-material';
import { loadPatients } from '../BaseURL';
import axios from 'axios';
import Swal from 'sweetalert2';



const PatientInformation = ({onEditPatient }) => {

  const [searchBy, setSearchBy] = useState({
    opdNo: true,
    name: false,
    insuranceNo: false,
    phoneNo: false,
    fingerPrint: false
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [entriesPerPage, setEntriesPerPage] = useState(20);
  const [activeFilter, setActiveFilter] = useState('today');
  const [isLoading, setIsLoading] = useState(true);

  const [allPatientData, setallPatientData] = useState([]);
  
  // New state for row selection
  const [selectedPatients, setSelectedPatients] = useState(new Set());
  const [selectedPatient, setSelectedPatient] = useState(null);

  // State for Patient Vitals Modal
  const [openPatient, setOpenPatient] = useState(false);
  const [vitalsInfo, setVitalsInfo] = useState(null);

  useEffect(() => {
    AllPatient()
  }, [])

  const AllPatient = async () => {
    try {
      setIsLoading(true);
      const response = await axios.get(loadPatients);
      if (response.data.success) {
        setallPatientData(response.data.results);
      }
    } catch (error) {
      await Swal.fire({
        title: 'Error',
        text: error.response?.data?.message || 'Failed Load Data',
        icon: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  }

  const handleSearchByChange = (field) => {
    setSearchBy(prev => ({
      ...prev,
      [field]: !prev[field]
    }));
  };

  // Enhanced row selection handlers
  const handleRowSelect = (patient) => {
    setSelectedPatient(patient);
    const newSelectedPatients = new Set(selectedPatients);
    
    if (selectedPatients.has(patient.id)) {
      newSelectedPatients.delete(patient.id);
    } else {
      newSelectedPatients.add(patient.id);
    }
    
    setSelectedPatients(newSelectedPatients);
  };

  const handleClearSelection = () => {
    setSelectedPatients(new Set());
    setSelectedPatient(null);
  };

  const handleSelectAll = () => {
    if (selectedPatients.size === searchFilteredData.slice(0, entriesPerPage).length) {
      // If all are selected, clear selection
      handleClearSelection();
    } else {
      // Select all visible patients
      const allVisibleIds = new Set(
        searchFilteredData.slice(0, entriesPerPage).map(patient => patient.id)
      );
      setSelectedPatients(allVisibleIds);
    }
  };

  const handleEditRecord = (patient) => {
    console.log('Edit record for patient:', patient);
    
    // Pass the entire patient object to the parent component
    if (onEditPatient) {
      onEditPatient(patient);
    }
  };



  

  const filterDataByPeriod = (data, period) => {
    const today = new Date();
    
    return data.filter(patient => {
      const visitDate = new Date(patient.reg_date);
      
      switch(period) {
        case 'today':
          // Since reg_date is in YYYY-MM-DD format, get today in the same format
          // Using local date to avoid timezone issues
          const todayStr = today.getFullYear() + '-' + 
                          String(today.getMonth() + 1).padStart(2, '0') + '-' + 
                          String(today.getDate()).padStart(2, '0');
          return patient.reg_date === todayStr;
          
        case 'week':
          const weekAgo = new Date(today);
          weekAgo.setDate(today.getDate() - 7);
          return visitDate >= weekAgo;
          
        case 'month':
          const monthAgo = new Date(today);
          monthAgo.setMonth(today.getMonth() - 1);
          return visitDate >= monthAgo;
          
        case 'year':
          const yearAgo = new Date(today);
          yearAgo.setFullYear(today.getFullYear() - 1);
          return visitDate >= yearAgo;
          
        case 'all':
          return true;
          
        default:
          return true;
      }
    });
  };

  const filteredData = filterDataByPeriod(allPatientData, activeFilter);
  
  const searchFilteredData = filteredData.filter(patient => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const searchFields = [];
    
    // Absolute number search for OPD Number (trans_code)
    if (searchBy.opdNo) {
      if (String(patient.trans_code) === searchTerm) {
        return true;
      }
    }
    
    // Absolute number search for Insurance Number
    if (searchBy.insuranceNo && patient.Insurance) {
      if (String(patient.Insurance) === searchTerm) {
        return true;
      }
    }
    
    // Handle name field (assuming it's always a string)
    if (searchBy.name) {
      searchFields.push(patient.name.toLowerCase());
    }
    
    // Handle phone field (might be string or number)
    if (searchBy.phoneNo) {
      searchFields.push(String(patient.tel).toLowerCase());
    }
    
    return searchFields.some(field => field.includes(searchLower));
  });

  // Loading skeleton component for table rows
  const LoadingSkeleton = () => (
    <>
      {[...Array(5)].map((_, index) => (
        <TableRow key={index}>
          <TableCell><Skeleton variant="rectangular" width={20} height={20} /></TableCell>
          <TableCell><Skeleton variant="text" width={60} /></TableCell>
          <TableCell><Skeleton variant="text" width={120} /></TableCell>
          <TableCell><Skeleton variant="rectangular" width={60} height={24} sx={{ borderRadius: 1 }} /></TableCell>
          <TableCell><Skeleton variant="text" width={40} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={100} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell><Skeleton variant="text" width={90} /></TableCell>
          <TableCell><Skeleton variant="text" width={80} /></TableCell>
          <TableCell sx={{ textAlign: 'center' }}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
              <Skeleton variant="circular" width={32} height={32} />
            </Box>
          </TableCell>
        </TableRow>
      ))}
    </>
  );
 
  return (
    <Box sx={{ p: 3, backgroundColor: '#f8fafc', minHeight: '100vh' }}>
      {/* Header with Add New Patient Button */}
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#1e293b', mb: 1 }}>
            Patient Information
          </Typography>
          <Typography variant="subtitle1" sx={{ color: '#64748b' }}>
           Welcome to the Patient Information System. Below is the list of your patients:
          </Typography>
          
        </Box>
        
      </Box>

      {/* Selection Status Alert */}
      {selectedPatients.size > 0 && (
        <Alert 
          severity="info" 
          sx={{ 
            mb: 3, 
            borderRadius: 2,
            '& .MuiAlert-message': {
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%'
            }
          }}
          action={
            <Button
              color="inherit"
              size="small"
              onClick={handleClearSelection}
              startIcon={<ClearIcon />}
              sx={{ textTransform: 'none' }}
            >
              Clear Selection
            </Button>
          }
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon fontSize="small" />
            <Typography>
              {selectedPatients.size} patient{selectedPatients.size !== 1 ? 's' : ''} selected
            </Typography>
          </Box>
        </Alert>
      )}

      {/* Search Section */}
      <Card sx={{ mb: 3, borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, color: '#374151', fontWeight: 600 }}>
            Search & Filter Patients
          </Typography>
          
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 3, mb: 3 }}>
            {/* Search By Checkboxes */}
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {[
                { key: 'opdNo', label: 'OPD No.' },
                { key: 'name', label: 'Name' },
                { key: 'insuranceNo', label: 'Insurance No.' },
                { key: 'phoneNo', label: 'Phone No.' },
              ].map((item) => (
                <FormControlLabel
                  key={item.key}
                  control={
                    <Checkbox
                      checked={searchBy[item.key]}
                      onChange={() => handleSearchByChange(item.key)}
                      disabled={isLoading}
                      sx={{ color: '#059669' }}
                    />
                  }
                  label={item.label}
                  sx={{ '& .MuiFormControlLabel-label': { color: '#374151', fontWeight: 500 } }}
                />
              ))}
            </Box>

            {/* Divider */}
            <Divider orientation="vertical" flexItem sx={{ height: 40, alignSelf: 'center' }} />

            {/* Time Filter Buttons */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography variant="subtitle2" sx={{ color: '#374151', fontWeight: 600, whiteSpace: 'nowrap' }}>
                Filter by Period:
              </Typography>
              <ButtonGroup variant="contained" sx={{ boxShadow: 'none' }}>
                {[
                  { key: 'today', label: 'Today' },
                  { key: 'week', label: 'This Week' },
                  { key: 'month', label: 'This Month' },
                  { key: 'year', label: 'This Year' },
                  { key: 'all', label: 'All' }
                ].map((filter) => (
                  <Button
                    key={filter.key}
                    onClick={() => setActiveFilter(filter.key)}
                    disabled={isLoading}
                    sx={{
                      backgroundColor: activeFilter === filter.key ? '#059669' : '#e2e8f0',
                      color: activeFilter === filter.key ? 'white' : '#64748b',
                      fontWeight: 600,
                      textTransform: 'none',
                      px: 2,
                      py: 1,
                      fontSize: '0.875rem',
                      '&:hover': {
                        backgroundColor: activeFilter === filter.key ? '#059669' : '#cbd5e1',
                      },
                      '&:disabled': {
                        backgroundColor: activeFilter === filter.key ? '#9ca3af' : '#f1f5f9',
                        color: activeFilter === filter.key ? '#e5e7eb' : '#9ca3af',
                      }
                    }}
                  >
                    {filter.label}
                  </Button>
                ))}
              </ButtonGroup>
            </Box>
          </Box>

          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
            <TextField
              variant="outlined"
              placeholder="Search patients..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              disabled={isLoading}
              sx={{ 
                flexGrow: 1, 
                minWidth: 300,
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
            <IconButton 
              disabled={isLoading}
              sx={{ 
                backgroundColor: '#059669', 
                color: 'white',
                borderRadius: 2,
                px: 2,
                '&:hover': { backgroundColor: '#059669' },
                '&:disabled': {
                  backgroundColor: '#9ca3af',
                  color: '#e5e7eb',
                }
              }}
            >
              <SearchIcon />
            </IconButton>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>Show</Typography>
              <FormControl size="small" sx={{ minWidth: 80 }}>
                <Select
                  value={entriesPerPage}
                  onChange={(e) => setEntriesPerPage(e.target.value)}
                  disabled={isLoading}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                   <MenuItem value={500}>500</MenuItem>
                   <MenuItem value={1000}>1000</MenuItem>
                </Select>
              </FormControl>
              <Typography sx={{ color: '#374151', fontWeight: 500 }}>entries</Typography>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Patient Table */}
      <Card sx={{ borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.05)' }}>
        <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: '#f8fafc' }}>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedPatients.size > 0 && 
                      selectedPatients.size < searchFilteredData.slice(0, entriesPerPage).length
                    }
                    checked={
                      searchFilteredData.slice(0, entriesPerPage).length > 0 &&
                      selectedPatients.size === searchFilteredData.slice(0, entriesPerPage).length
                    }
                    onChange={handleSelectAll}
                    disabled={isLoading || searchFilteredData.length === 0}
                    sx={{ color: '#059669' }}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Patient ID</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Gender</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Age</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Insurance #</TableCell>
                {/*  Beside the membership number, because the two are read
                     together — a claim needs both, and an officer checking one
                     is checking the other. */}
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>CCC</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Address</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Telephone</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Occupation</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151' }}>Visit Date</TableCell>
                <TableCell sx={{ fontWeight: 'bold', color: '#374151', textAlign: 'center' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <LoadingSkeleton />
              ) : searchFilteredData.length === 0 ? (
                <TableRow>
                  {/*  12 now: checkbox, ID, Name, Gender, Age, Insurance #,
                       CCC, Address, Telephone, Occupation, Visit Date, Actions.
                       A colSpan short of the header leaves the "no patients"
                       message boxed into part of the width. */}
                  <TableCell colSpan={12} sx={{ textAlign: 'center', py: 4 }}>
                    <Typography color="text.secondary" sx={{ fontSize: '16px' }}>
                      {searchTerm ? 'No patients found matching your search criteria' : 'No patient records found for this period'}
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                searchFilteredData.slice(0, entriesPerPage).map((patient, index) => (
                  <TableRow 
                    key={patient.id}
                    selected={selectedPatients.has(patient.id)}
                    sx={{ 
                      '&:hover': { backgroundColor: '#f8fafc' },
                      '&:nth-of-type(even)': { backgroundColor: '#fafbfc' },
                      '&.Mui-selected': {
                        backgroundColor: '#ecfdf5 !important',
                        '&:hover': {
                          backgroundColor: '#d1fae5 !important',
                        }
                      }
                    }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox
                        checked={selectedPatients.has(patient.id)}
                        onChange={() => handleRowSelect(patient)}
                        sx={{ color: '#059669' }}
                      />
                    </TableCell>
                    <TableCell sx={{ fontWeight: 600, color: '#3b82f6' }}>{patient.id}</TableCell>
                    <TableCell sx={{ fontWeight: 500 }}>{patient.fullName}</TableCell>
                    <TableCell>
                      <Chip 
                        label={patient.gender} 
                        size="small"
                        sx={{
                          backgroundColor: patient.gender === 'Male' ? '#dbeafe' : '#fce7f3',
                          color: patient.gender === 'Male' || patient.gender === 'MALE'  ? '#1d4ed8' : '#be185d',
                          fontWeight: 500
                        }}
                      />
                    </TableCell>
                    <TableCell>{patient.age}</TableCell>
                    <TableCell>{patient.Insurance}</TableCell>
                    {/*  The claim check code for THIS VISIT. It is not a
                         property of the patient — it comes off the member's card
                         on the day — so a patient seen twice can legitimately
                         show one on one row and not the other.

                         An em dash rather than an empty cell where there is
                         none: a blank reads as a rendering fault, and a missing
                         CCC is exactly what an officer is scanning this column
                         for. */}
                    <TableCell sx={{
                      fontFamily: patient.ccc ? 'monospace' : 'inherit',
                      color: patient.ccc ? '#111827' : '#9ca3af',
                      fontWeight: patient.ccc ? 600 : 400,
                    }}>
                      {patient.ccc || '—'}
                    </TableCell>
                    <TableCell>{patient.res}</TableCell>
                    <TableCell>{patient.tel}</TableCell>
                    <TableCell>{patient.occ}</TableCell>
                    <TableCell sx={{ color: '#059669', fontWeight: 500 }}>{patient.reg_date}</TableCell>
                    <TableCell sx={{ textAlign: 'center' }}>
                      <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                        
                        {/* Edit Button */}
                        <IconButton
                          onClick={() => handleEditRecord(patient)}
                          sx={{
                            backgroundColor: '#f0f9ff',
                            color: '#0ea5e9',
                            '&:hover': {
                              backgroundColor: '#e0f2fe',
                              color: '#0284c7',
                            },
                            borderRadius: 2,
                            p: 1
                          }}
                          title={`Edit ${patient.fullName}'s record`}
                        >
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <Box sx={{ p: 3, backgroundColor: '#f8fafc', borderRadius: '0 0 8px 8px' }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <CircularProgress size={16} sx={{ color: '#059669' }} />
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
                Loading patient records...
              </Typography>
            </Box>
          ) : (
            <Typography variant="body2" sx={{ color: '#64748b', fontWeight: 500 }}>
              Showing {Math.min(searchFilteredData.length, entriesPerPage)} of {searchFilteredData.length} entries
              {selectedPatients.size > 0 && (
                <Chip 
                  label={`${selectedPatients.size} selected`} 
                  size="small" 
                  sx={{ ml: 2, backgroundColor: '#059669', color: 'white' }} 
                />
              )}
              {activeFilter !== 'today' && (
                <Chip 
                  label={`Filtered by: ${activeFilter}`} 
                  size="small" 
                  sx={{ ml: 2, backgroundColor: '#3b82f6', color: 'white' }} 
                />
              )}
            </Typography>
          )}
        </Box>
      </Card>

    </Box>
  );
};

export default PatientInformation;